import { useState } from "react"
import {
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  IconButton,
  Divider,
  TextField,
  Avatar,
  Tooltip,
} from "@mui/material"
import {
  FiberNew as FiberNewIcon,
  PlayArrow as PlayArrowIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Receipt as ReceiptIcon,
  AccountBalance as AccountBalanceIcon,
  Assessment as AssessmentIcon,
  Refresh as RefreshIcon,
  CreditCard as PaymentIcon,
} from "@mui/icons-material"
import { useApiQuery } from "../../hooks/useApi"
import AdminReportGenerator from "./AdminReportGenerator"
import ClientBalanceCard from "../../components/ClientBalanceCard"
import AdminLiveActivityFeed from "../../components/activity/AdminLiveActivityFeed"

// Modern SaaS Metric Card component matching the rest of the application
function MetricCard({ title, value, icon, bgColor, iconColor, subtitle }) {
  return (
    <Card
      sx={{
        height: "100%",
        borderRadius: "12px",
        border: "1px solid #f1f5f9",
        // boxShadow: "0 1px 3px 0 rgba(15, 23, 42, 0.05)",
        // transition: "all 0.2s ease-in-out",
        backgroundColor: "#ffffff",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: "0 5px 10px -3px rgba(15, 23, 42, 0.08)",
        },
      }}
    >
      <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box>
            <Typography
              variant="overline"
              sx={{ fontWeight: 600, letterSpacing: "0.05em", color: "#64748b", display: "block" }}
            >
              {title}
            </Typography>
            <Typography variant="h3" component="h2" sx={{ fontWeight: 700, color: "#0f172a", my: 0.5 }}>
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="caption" sx={{ color: "#94a3b8", fontWeight: 500 }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          {/* <Avatar
            sx={{
              backgroundColor: bgColor,
              color: iconColor,
              width: 48,
              height: 48,
              borderRadius: "10px",
              flexShrink: 0,
            }}
          >
            {icon}
          </Avatar> */}
        </Box>
      </CardContent>
    </Card>
  )
}

export default function AdminDashboard() {
  const [dateFilter, setDateFilter] = useState("today")
  const [startDate, setStartDate] = useState("")
  const [endDate, setEndDate] = useState("")
  const [reportDialogOpen, setReportDialogOpen] = useState(false)

  // Build query params
  const queryParams = new URLSearchParams()
  if (dateFilter === "custom" && startDate && endDate) {
    queryParams.append("startDate", startDate)
    queryParams.append("endDate", endDate)
  } else {
    queryParams.append("filter", dateFilter)
  }

  const { data: stats, isLoading, error, refetch } = useApiQuery(
    ["admin-stats", dateFilter, startDate, endDate],
    `/admin/stats?${queryParams.toString()}`
  )

  const getFilterLabel = () => {
    switch (dateFilter) {
      case "today": return "Today"
      case "30days": return "Last 30 Days"
      case "month": return "This Month"
      case "all": return "All Time"
      case "custom":
        if (startDate && endDate) {
          return `${new Date(startDate).toLocaleDateString()} - ${new Date(endDate).toLocaleDateString()}`
        }
        return "Custom Range"
      default: return "Today"
    }
  }

  const handleDateFilterChange = (newFilter) => {
    setDateFilter(newFilter)
    if (newFilter !== "custom") {
      setStartDate("")
      setEndDate("")
    }
  }

  const handleRefresh = () => {
    refetch()
  }

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress size={48} thickness={4} />
      </Box>
    )
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2, borderRadius: "10px" }}>
        Error loading dashboard: {error.message}
      </Alert>
    )
  }

  return (
    <Box sx={{ pb: 3 }}>
      {/* HEADER WITH INTEGRATED FILTER CONTROLS IN THE SAME ROW */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, flexWrap: "wrap", gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: "#0f172a", tracking: "-0.02em" }}>
            Admin Dashboard
          </Typography>
          <Typography variant="body2" sx={{ color: "#64748b", mt: 0.25 }}>
            Real-time operations, metrics & live job activity feed
          </Typography>
        </Box>

        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
          {/* Period Filter Dropdown */}
          <FormControl size="small" sx={{ minWidth: 150, backgroundColor: "#ffffff" }}>
            <InputLabel id="period-select-label">Period</InputLabel>
            <Select
              labelId="period-select-label"
              value={dateFilter}
              label="Period"
              onChange={(e) => handleDateFilterChange(e.target.value)}
              sx={{ borderRadius: "8px", fontSize: "0.875rem" }}
            >
              <MenuItem value="today">Today</MenuItem>
              <MenuItem value="30days">Last 30 Days</MenuItem>
              <MenuItem value="month">This Month</MenuItem>
              <MenuItem value="all">All Time</MenuItem>
              <MenuItem value="custom">Custom Range</MenuItem>
            </Select>
          </FormControl>

          {/* Custom Date Range Inputs */}
          {dateFilter === "custom" && (
            <>
              <TextField
                type="date"
                label="Start Date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
                sx={{ minWidth: 140, backgroundColor: "#ffffff" }}
              />
              <TextField
                type="date"
                label="End Date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
                sx={{ minWidth: 140, backgroundColor: "#ffffff" }}
              />
            </>
          )}

          {/* Refresh Button */}
          <Tooltip title="Refresh Dashboard">
            <IconButton
              onClick={handleRefresh}
              size="small"
              sx={{
                backgroundColor: "#ffffff",
                border: "1px solid #cbd5e1",
                color: "#475569",
                p: 0.9,
                "&:hover": { backgroundColor: "#f8fafc" },
              }}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          {/* Generate Report Button */}
          <Button
            variant="contained"
            startIcon={<AssessmentIcon fontSize="small" />}
            onClick={() => setReportDialogOpen(true)}
            size="small"
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 600,
              px: 2,
              py: 0.8,
              backgroundColor: "#1976d2",
              boxShadow: "0 1px 2px 0 rgba(37,99,235,0.2)",
              "&:hover": { backgroundColor: "#1d4ed8" },
            }}
          >
            Generate Report
          </Button>
        </Stack>
      </Box>

      {/* 2-COLUMN WORKSPACE LAYOUT */}
      <Grid container spacing={2.5}>
        {/* LEFT COLUMN: Operations Overview & Financial Summary */}
        <Grid item xs={12} md={7.5} lg={8}>
          {/* SECTION 1: PERIOD OVERVIEW */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#0f172a", mb: 1.5 }}>
              {getFilterLabel()} Overview
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}>
                <MetricCard
                  title="New Jobs"
                  value={stats?.newJobsToday || 0}
                  subtitle={getFilterLabel()}
                  bgColor="#eff6ff"
                  iconColor="#2563eb"
                  icon={<FiberNewIcon fontSize="small" />}
                />
              </Grid>

              <Grid item xs={6} sm={3}>
                <MetricCard
                  title="Processing"
                  value={stats?.processingJobsToday || 0}
                  subtitle={getFilterLabel()}
                  bgColor="#fffbeb"
                  iconColor="#d97706"
                  icon={<PlayArrowIcon fontSize="small" />}
                />
              </Grid>

              <Grid item xs={6} sm={3}>
                <MetricCard
                  title="Completed"
                  value={stats?.completedJobsToday || 0}
                  subtitle={getFilterLabel()}
                  bgColor="#f0fdf4"
                  iconColor="#16a34a"
                  icon={<CheckCircleIcon fontSize="small" />}
                />
              </Grid>

              <Grid item xs={6} sm={3}>
                <MetricCard
                  title="Cancelled"
                  value={stats?.cancelledJobsToday || 0}
                  subtitle={getFilterLabel()}
                  bgColor="#fff1f2"
                  iconColor="#e11d48"
                  icon={<CancelIcon fontSize="small" />}
                />
              </Grid>
            </Grid>
          </Box>

          {/* SECTION 2: FINANCIAL SUMMARY */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#0f172a", mb: 1.5 }}>
              Financial Summary
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <MetricCard
                  title="Receipts Created"
                  value={stats?.receiptsCreatedToday || 0}
                  subtitle={`₹${(stats?.totalReceiptsAmount || 0).toLocaleString()}`}
                  bgColor="#eef2ff"
                  iconColor="#4f46e5"
                  icon={<ReceiptIcon fontSize="small" />}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <MetricCard
                  title="Jobs Settled"
                  value={stats?.jobsSettledToday || 0}
                  subtitle={`₹${(stats?.totalSettledAmount || 0).toLocaleString()}`}
                  bgColor="#f0fdfa"
                  iconColor="#0d9488"
                  icon={<AccountBalanceIcon fontSize="small" />}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <ClientBalanceCard stats={stats} />
              </Grid>
            </Grid>
          </Box>

          {/* SECTION 3: PAYMENT BREAKDOWN */}
          <Paper
            sx={{
              p: 2.5,
              borderRadius: "12px",
              border: "1px solid #f1f5f9",
              boxShadow: "0 1px 3px 0 rgba(15,23,42,0.05)",
              backgroundColor: "#ffffff",
              mb: 3,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
              <PaymentIcon sx={{ color: "#475569", fontSize: 20 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#0f172a" }}>
                Payment Breakdown
              </Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />

            {stats?.receiptsByPaymentMethod &&
              Object.keys(stats.receiptsByPaymentMethod).length > 0 ? (
              <TableContainer sx={{ maxHeight: 320 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, color: "#475569", backgroundColor: "#f8fafc" }}>
                        Method
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: "#475569", backgroundColor: "#f8fafc" }}>
                        Count
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: "#475569", backgroundColor: "#f8fafc" }}>
                        Total Amount
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, color: "#475569", backgroundColor: "#f8fafc" }}>
                        Bank
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: "#475569", backgroundColor: "#f8fafc" }}>
                        Bank Amount
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {Object.entries(stats.receiptsByPaymentMethod).map(([method, data]) =>
                      Object.entries(data.banks || { "N/A": data.total }).map(
                        ([bank, bankAmount], index) => (
                          <TableRow key={`${method}-${bank}`} hover>
                            <TableCell>
                              {index === 0 ? (
                                <Chip
                                  label={method.toUpperCase()}
                                  size="small"
                                  sx={{
                                    fontWeight: 700,
                                    fontSize: "0.68rem",
                                    borderRadius: "4px",
                                    backgroundColor: "#e2e8f0",
                                    color: "#1e293b",
                                  }}
                                />
                              ) : (
                                ""
                              )}
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: index === 0 ? 600 : 400 }}>
                              {index === 0 ? data.count : ""}
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: index === 0 ? 700 : 400, color: index === 0 ? "#0f172a" : "inherit" }}>
                              {index === 0 ? `₹${data.total.toLocaleString()}` : ""}
                            </TableCell>
                            <TableCell sx={{ color: "#475569" }}>{bank}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, color: "#0f172a" }}>
                              ₹{bankAmount.toLocaleString()}
                            </TableCell>
                          </TableRow>
                        )
                      )
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box sx={{ textAlign: "center", py: 5, color: "#94a3b8" }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  No receipts recorded for this period.
                </Typography>
                <Typography variant="caption">
                  New fee receipts created by Fee Collectors will automatically appear here.
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* RIGHT COLUMN: PINNED PERSISTENT LIVE JOB ACTIVITY PANEL */}
        <Grid item xs={12} md={4.5} lg={4}>
          <Box
            sx={{
              position: { xs: "static", md: "sticky" },
              top: 20,
              height: { xs: "520px", md: "calc(100vh - 140px)" },
              minHeight: { md: 520 },
              mb: { xs: 3, md: 0 },
            }}
          >
            <AdminLiveActivityFeed />
          </Box>
        </Grid>
      </Grid>

      {/* REPORT MODAL */}
      <AdminReportGenerator
        open={reportDialogOpen}
        onClose={() => setReportDialogOpen(false)}
      />
    </Box>
  )
}